import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from 'react';
import medEventApi, { generateRandomUserId, ApiError } from '../apis/apis.js';

const SESSION_ACTIONS = {
  SET_USER_ID: 'SET_USER_ID',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_SESSION_DATA: 'SET_SESSION_DATA',
  SET_CURRENT_QUESTION: 'SET_CURRENT_QUESTION',
  START_SESSION: 'START_SESSION',
  END_SESSION: 'END_SESSION',
  RESET_SESSION: 'RESET_SESSION',
  SESSION_COMPLETED: 'SESSION_COMPLETED',
};

const initialState = {
  userId: null,
  sessionId: null,
  activeSessionData: null, // Store full active session data including start_date
  liveUrl: null, // Store live_url from active session
  formUrl: null, // Store form_url from active session
  currentQuestion: null,
  currentQuestionNumber: 0,
  totalQuestions: 0,
  equivalentCorrectAnswers: 0,
  isLoading: false,
  error: null,
  sessionStarted: false,
  sessionEnded: false,
  sessionCompleted: false,
  serverDate: null,
};

const sessionReducer = (state, action) => {
  switch (action.type) {
    case SESSION_ACTIONS.SET_USER_ID:
      return { ...state, userId: action.payload };

    case SESSION_ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case SESSION_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };

    case SESSION_ACTIONS.SET_SESSION_DATA:
      return {
        ...state,
        ...action.payload,
        isLoading: false,
        error: null,
      };

    case SESSION_ACTIONS.SET_CURRENT_QUESTION:
      return {
        ...state,
        currentQuestion: action.payload.question,
        currentQuestionNumber: action.payload.questionNumber,
        equivalentCorrectAnswers:
          action.payload.correctAnswers || state.equivalentCorrectAnswers,
        isLoading: false,
        error: null,
      };

    case SESSION_ACTIONS.START_SESSION:
      return {
        ...state,
        sessionStarted: true,
        sessionEnded: false,
        isLoading: false,
        error: null,
        ...action.payload,
      };

    case SESSION_ACTIONS.END_SESSION:
      localStorage.removeItem('quiz-session-state');
      return {
        ...state,
        sessionEnded: true,
        sessionStarted: false,
      };

    case SESSION_ACTIONS.RESET_SESSION:
      localStorage.removeItem('quiz-session-state');
      return {
        ...initialState,
        userId: state.userId,
      };

    case SESSION_ACTIONS.SESSION_COMPLETED:
      localStorage.removeItem('quiz-session-state');
      return {
        ...state,
        sessionCompleted: true,
        sessionStarted: false,
        isLoading: false,
      };

    default:
      return state;
  }
};

const SessionContext = createContext();
const SessionDispatchContext = createContext();

export const SessionProvider = ({ children }) => {
  const [state, dispatch] = useReducer(sessionReducer, initialState);

  useEffect(() => {
    let userId = localStorage.getItem('quiz-user-id');
    if (!userId) {
      userId = generateRandomUserId();
      localStorage.setItem('quiz-user-id', userId);
    }
    dispatch({ type: SESSION_ACTIONS.SET_USER_ID, payload: userId });
  }, []);

  useEffect(() => {
    if (state.sessionStarted && state.sessionId) {
      const sessionData = {
        sessionId: state.sessionId,
        currentQuestion: state.currentQuestion,
        currentQuestionNumber: state.currentQuestionNumber,
        totalQuestions: state.totalQuestions,
        equivalentCorrectAnswers: state.equivalentCorrectAnswers,
        sessionStarted: state.sessionStarted,
        userId: state.userId,
      };
      localStorage.setItem('quiz-session-state', JSON.stringify(sessionData));
      localStorage.setItem('quiz-user-id', state.userId);
    }
  }, [state]);

  useEffect(() => {
    const savedState = localStorage.getItem('quiz-session-state');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        dispatch({
          type: SESSION_ACTIONS.SET_SESSION_DATA,
          payload: parsedState,
        });
      } catch (error) {
        console.error('Failed to restore session state:', error);
        localStorage.removeItem('quiz-session-state');
      }
    }
  }, []);

  return (
    <SessionContext.Provider value={state}>
      <SessionDispatchContext.Provider value={dispatch}>
        {children}
      </SessionDispatchContext.Provider>
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const useSessionDispatch = () => {
  const context = useContext(SessionDispatchContext);
  if (!context) {
    throw new Error('useSessionDispatch must be used within a SessionProvider');
  }
  return context;
};

export const useSessionActions = () => {
  const dispatch = useSessionDispatch();
  const session = useSession();

  const checkActiveSession = useCallback(async () => {
    if (!session.userId) {
      return;
    }

    dispatch({ type: SESSION_ACTIONS.SET_LOADING, payload: true });

    try {
      const response = await medEventApi.getActiveSession(session.userId);

      if (!response.data || response.data === null) {
        dispatch({ type: SESSION_ACTIONS.SESSION_COMPLETED });
        dispatch({ type: SESSION_ACTIONS.SET_LOADING, payload: false });

        const completionError = new Error('SESSION_COMPLETED');
        completionError.isCompletion = true;
        throw completionError;
      }

      dispatch({
        type: SESSION_ACTIONS.SET_SESSION_DATA,
        payload: {
          sessionId: response.data.session_id,
          activeSessionData: response.data,
          liveUrl: response.data.live_url,
          formUrl: response.data.form_url
        },
      });

      return response;
    } catch (error) {
      console.error('Failed to check active session:', error);

      if (error.message === 'SESSION_COMPLETED' || error.isCompletion) {
        throw error;
      }

      dispatch({
        type: SESSION_ACTIONS.SET_ERROR,
        payload:
          error instanceof ApiError
            ? error.message
            : 'Failed to check session status',
      });
      throw error;
    }
  }, [session.userId, dispatch]);

  const startSession = useCallback(
    async (sessionId) => {
      if (!session.userId) {
        throw new Error('User ID not initialized');
      }

      dispatch({ type: SESSION_ACTIONS.SET_LOADING, payload: true });

      try {
        const response = await medEventApi.startSession(
          sessionId,
          session.userId
        );

        dispatch({
          type: SESSION_ACTIONS.START_SESSION,
          payload: {
            sessionId,
            currentQuestion: response.data.current_question,
            currentQuestionNumber: response.data.current_question_number,
            totalQuestions: response.data.total_questions_count,
            equivalentCorrectAnswers: response.data.equivalent_correct_answers,
            serverDate: response.data.server_date,
            isLoading: false,
          },
        });

        return response.data;
      } catch (error) {
        console.error('Failed to start session:', error);
        dispatch({
          type: SESSION_ACTIONS.SET_ERROR,
          payload:
            error instanceof ApiError
              ? error.message
              : 'Failed to start session',
        });
        throw error;
      }
    },
    [session.userId, dispatch]
  );

  const submitAnswer = useCallback(
    async (answerNumber, answerText) => {
      if (!session.sessionId || !session.currentQuestion) {
        throw new Error('No active session or question');
      }

      dispatch({ type: SESSION_ACTIONS.SET_LOADING, payload: true });

      try {
        const response = await medEventApi.respondToQuestion({
          sessionId: session.sessionId,
          questionNumber: session.currentQuestionNumber,
          answerNumber,
          answerText,
          webRandomUserId: session.userId,
        });

        if (response.data.session_completed || !response.data.next_question) {
          dispatch({ type: SESSION_ACTIONS.END_SESSION });
        } else if (response.data.next_question) {
          dispatch({
            type: SESSION_ACTIONS.SET_CURRENT_QUESTION,
            payload: {
              question: response.data.next_question,
              questionNumber: response.data.next_question_number,
              correctAnswers: response.data.equivalent_correct_answers,
            },
          });
        }

        dispatch({ type: SESSION_ACTIONS.SET_LOADING, payload: false });
        return response.data;
      } catch (error) {
        console.error('Failed to submit answer:', error);
        dispatch({
          type: SESSION_ACTIONS.SET_ERROR,
          payload:
            error instanceof ApiError
              ? error.message
              : 'Failed to submit answer',
        });
        throw error;
      }
    },
    [
      session.sessionId,
      session.currentQuestion,
      session.currentQuestionNumber,
      session.userId,
      dispatch,
    ]
  );

  const skipQuestion = useCallback(async () => {
    return submitAnswer(null, null);
  }, [submitAnswer]);

  const resetSession = useCallback(() => {
    dispatch({ type: SESSION_ACTIONS.RESET_SESSION });
  }, [dispatch]);

  const clearUserSession = useCallback(() => {
    localStorage.removeItem('quiz-session-state');
    dispatch({ type: SESSION_ACTIONS.RESET_SESSION });
  }, [dispatch]);

  return {
    checkActiveSession,
    startSession,
    submitAnswer,
    skipQuestion,
    resetSession,
    clearUserSession,
  };
};

// Hook to check if today is the event day or after (show countdown interface)
export const useIsEventDay = () => {
  const session = useSession();

  if (!session.activeSessionData?.start_date) {
    return null;
  }

  const today = new Date();
  const eventDate = new Date(session.activeSessionData.start_date);
  
  const todayStartOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const eventStartOfDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
  
  return todayStartOfDay >= eventStartOfDay;
};

// Hook to check if the event has started (countdown reached 0)
export const useIsEventStarted = () => {
  const session = useSession();

  if (!session.activeSessionData?.start_date) {
    return null;
  }
  
  return new Date() >= new Date(session.activeSessionData.start_date);
};

// Hook to get the live URL from session data
export const useLiveUrl = () => {
  const session = useSession();
  return session.liveUrl;
};

// Hook to get the form URL from session data
export const useFormUrl = () => {
  const session = useSession();
  return session.formUrl;
};

export { SESSION_ACTIONS };
